import { useEffect, useState } from 'react'

const LANDSCAPE_QUERY = '(orientation: landscape)'
const DESKTOP_FINE_POINTER_QUERY = '(hover: hover) and (pointer: fine)'

/** Mobile Capture route is phone-first; desktop browser windows are exempt. */
export function shouldShowMobileCaptureOrientationGuard(): boolean {
  if (typeof window === 'undefined') return false
  if (!window.matchMedia(LANDSCAPE_QUERY).matches) return false
  if (window.matchMedia(DESKTOP_FINE_POINTER_QUERY).matches) return false
  const w = window.innerWidth
  const h = window.innerHeight
  if (w >= 1024 && h >= 700) return false
  return true
}

/** Best-effort portrait lock — silent fallback when unsupported (e.g. iPhone Safari). */
export function tryProgressivePortraitOrientationLock(): void {
  if (typeof window === 'undefined') return
  const orientation = screen.orientation as ScreenOrientation & {
    lock?: (orientation: OrientationLockType) => Promise<void>
  }
  orientation?.lock?.('portrait').catch(() => undefined)
}

/**
 * Presentation-only landscape guard for Mobile Capture.
 * Does not touch session, queue, or network — overlay only.
 */
export function useMobileCaptureOrientation(): { showOrientationGuard: boolean } {
  const [showOrientationGuard, setShowOrientationGuard] = useState(() => shouldShowMobileCaptureOrientationGuard())

  useEffect(() => {
    tryProgressivePortraitOrientationLock()
  }, [])

  useEffect(() => {
    const landscapeMq = window.matchMedia(LANDSCAPE_QUERY)
    const desktopMq = window.matchMedia(DESKTOP_FINE_POINTER_QUERY)

    const update = () => {
      setShowOrientationGuard(shouldShowMobileCaptureOrientationGuard())
    }

    update()
    landscapeMq.addEventListener('change', update)
    desktopMq.addEventListener('change', update)
    window.addEventListener('resize', update)
    return () => {
      landscapeMq.removeEventListener('change', update)
      desktopMq.removeEventListener('change', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return { showOrientationGuard }
}
